<template>
    <div>
        <b-overlay :show="isLoading" :class="{'position-fixed w-100 h-100': isLoading}" rounded="sm" spinner-variant="primary" variant="dark">
            <app-header></app-header>

            <router-view></router-view>

            <app-footer></app-footer>

            <cookie-law></cookie-law>
        </b-overlay>
    </div>
</template>

<script>
    import { useToast } from "vue-toastification";

    import AppHeader from '@/components/views/header/app-header.vue';
    import AppFooter from '@/components/views/footer/app-footer.vue';
    import MainPage from '@/components/views/main-page/index.vue'
    import { useUserStore } from '@/stores/user';
    import erorrToastWithLink from "./common/toasts/erorr-toast-with-link.vue";
    import CookieLaw from "./common/cookie-law.vue";
    import Cookies from 'js-cookie';
    import { commonService } from "@/services";
    import seoMixin from "@/mixins/seo-mixin";

    export default {
        setup() {
            const toast = useToast();
            const userStore = useUserStore();

            return { toast, userStore };
        },

        components: {
            'app-header': AppHeader,
            'app-footer': AppFooter,
            'main-page': MainPage,
            'cookie-law': CookieLaw
        },

        data() {
            return {
                progressBarStarted: false,
                isLoading: false
            };
        },

        mixins: [seoMixin],

        async mounted() {
            await this.setLanguage();
            this.finishProgressBar();
            this.setRelCanonical();
        },

        created() {
            this.startProgressBar();
            this.initRouter();
            this.initAjax();
        },

        methods: {
            async setLanguage(reload = true) {
                let langCookie = Cookies.get('.Nop.Culture');
                var match = langCookie.match(/c=([a-zA-Z]{2})/);
                var lang = match ? match[1] : null;

                if (!lang) {
                    lang = 'en';
                    await this.changeLang(lang, false);
                } else {
                     this.$i18n.locale = lang;
                }
            },

            async changeLang(lang, reload = true) {
                this.$i18n.locale = lang;

                await commonService.setLanguage(this.getLanguageIdByName(lang));

                if (reload)
                    window.location.reload();
            },

            startProgressBar() {
                this.$Progress.start();
                this.progressBarStarted = true;
            },

            finishProgressBar() {
                this.$Progress.finish();
                this.progressBarStarted = false;
            },

            failProgressBar() {
                this.$Progress.fail();
                this.progressBarStarted = false;
            },

            initRouter() {
                this.$router.beforeEach((to, from) => {
                    if (to.meta.progress)
                        this.$Progress.parseMeta(to.meta.progress);
                    this.startProgressBar();
                });

                this.$router.afterEach((to, from) => {
                    this.finishProgressBar();
                });
            },

            initAjax() {
                this.axios.interceptors.request.use(config => {
                    if (!this.progressBarStarted && this.canShowGlobalLoader(config))
                        this.startProgressBar();
                    return config;
                }, error => {
                    if (error.request) {
                        if (this.canShowGlobalLoader(error.request.config))
                            this.failProgressBar();
                    }
                    else {
                        this.toast.error('Oops! ' + error.message);
                        this.failProgressBar();
                    }
                    return Promise.reject(error);
                });
                this.axios.interceptors.response.use(response => {
                    if (this.canShowGlobalLoader(response.config))
                        this.finishProgressBar();
                    return response;
                }, async error => {
                    if (error.response) {
                        if (this.canShowGlobalLoader(error.response.config)) {
                            this.failProgressBar();
                            this.hideOverlay();
                        }

                        if (error.response.status === 400) {
                            const content = {
                                component: erorrToastWithLink,
                                props: {
                                    errorMessage: await this.getNopcommerceErrorMessage(error)
                                }
                            };

                            this.toast.error(content);
                        }

                        if (error.response.status === 404) {
                            this.$router.replace({ name: 'not-found' });
                        }

                        if (error.response.status === 500) {
                            if (error.response.data.error)
                                this.toast.error(error.response.data.message);
                            else
                                this.toast.error(error.message);
                        }
                    }
                    else {
                        this.toast.error(error.message);

                        this.failProgressBar();
                        this.hideOverlay();
                    }

                    return Promise.reject(error);
                });
            },

            canShowGlobalLoader(config) {
                if (!config)
                    return true;
                return config.globalLoader !== false;
            },

            successToast(message) {
                this.toast.success(message, { icon: 'check' });
            },

            errorToast(message) {
                this.toast.error(message, { icon: 'exclamation' });
            },

            async getNopcommerceErrorMessage(error) {
                if (error.response.data instanceof Array)
                    return error.response.data[0];

                if (error.response.data && error.response.data.errors) {
                    let message = '';

                    for (let prop in error.response.data.errors)
                        message = error.response.data.errors[prop] instanceof Array
                            ? error.response.data.errors[prop][0] : error.response.data.errors[prop];

                    return message;
                }

                if (error.request && error.request.responseType === "blob")
                    return await error.response.data.text();

                return error.response.data.message || error.response.data.inner_exception_message || error.response.data || error.message;
            },

            showOverlay() {
                this.isLoading = true;
            },

            hideOverlay() {
                this.isLoading = false;
            },

            setRelCanonical() {
                let linkTag = document.createElement('link');

                linkTag.setAttribute('rel', 'canonical');
                linkTag.href = window.location.href;

                document.head.appendChild(linkTag);
            }
        }
    };
</script>
